import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import styled, { css } from 'styled-components';
import { Alert } from '../../../components/designSystem';
import { Media } from 'src/utils/Media';

import TextSegment from './chapter/textSegment';
import Survey from './survey/survey';
import Cover from './cover/cover';
import language from '../../../config/language';
import {
  DeviceMinWidths,
  MaxTextWidth,
  MediaQuery,
} from '../../../constants/size';


import { toggleAppSidebar } from '../../../modules/app';
import {
  saveReadingData,
  updateSpineIndex,
  fetchReadingData,
  updateReadingData,
  resetReader,
} from '../../../modules/readerApp';
import { fetchCurrentBookAsReader } from '../../../modules/book';
import { submitComment } from '../../../modules/comment';
import ChapterCommentsList from '../../comment/commentsList';
import InlineCommentsList from '../../comment/inlineCommentsList';
import ChapterMenu from './footer/chapterMenu';
import { PaddedWrapper } from '../../../style';

// start monitoring and tracking
import {
  addTimeSpent,
  trackTimeSpent,
  updateFocus,
} from '../../../modules/monitor';

import {
  CLOSED_BOOK,
  REACHED_PERCENTILE,
  TIME_SPENT,
  track as doTrack,
} from '../../../utils/tracker';
import { authUtil, featureToggleUtil } from '../../../utils';
import appUtil from '../../../utils/appUtil';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import ChapterAudioPlayer from './audio/chapterAudioPlayer';
import ReaderAudioAccess, {
  resolveReaderAudioContentVersionId,
} from './audio/readerAudioAccess';
import { useHostAppProxy } from 'src/contexts/HostAppProxyContext';
import {
  blockReaderContentAction,
  blockReaderContextMenu,
  blockReaderPrintShortcut,
} from './readerContentProtection';
import {
  getOrderedKeys,
  getTextPosition as getReaderTextPosition,
  scrollToAnchor as scrollReaderToAnchor,
} from './audio/readerDomAnchors';
import {
  continueAfterAudioEnded,
  continueAfterSurvey,
  continueFromNativeAudioEvent,
  getNativeAudioContinuationQueue,
} from './audio/audioContinuation';
import { shouldPrepareReaderAudio } from './audio/audioDeliveryPolicy';
import ReaderWrapper from './readerWrapper';

// end monitoring and tracking

const MaxWidthWrapper = styled.div`
  ${({ maxWidth = `${MaxTextWidth}px` }) =>
    maxWidth &&
    css`
      max-width: ${maxWidth}px;
    `}
`;

const ChapterWrapper = styled.div`
  display: grid;
  padding-left: 1em;
  padding-right: 1em;
  padding-bottom: ${({ $audioDockHeight = 0 }) =>
    $audioDockHeight ? `${$audioDockHeight + 24}px` : '0'};
  ${({ sidebarVisible }) =>
    !sidebarVisible
      ? css`
          grid-template-columns: 1fr minmax(0, ${MaxTextWidth}px) 1fr;
        `
      : css`
          grid-template-columns: minmax(0, ${MaxTextWidth}px) 1fr;
        `}
`;

const SidebarCommentsWrapper = styled.div`
  ${MediaQuery.phone} {
    display: none;
  }
  max-width: var(--sidebar-width);
  width: var(--sidebar-width);
`;

const ReaderTextStage = styled.div`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ReaderTitle = styled.h1`
  color: var(--br-text);
  font-size: 2em;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 2em;
  text-align: center;
`;

const ReaderDivider = styled.div`
  height: 1em;
`;

const ReaderProgress = styled.progress`
  position: fixed;
  top: 40px;
  width: 100%;
  height: 1em;
  accent-color: #e58036;
`;

const trackingInterval = 10; // seconds
const monitoringInterval = 1; // seconds
const maxInActivityTime = 300; // seconds

const Reader = () => {
  const dispatch = useDispatch();
  const { settings: hostAppSettings, sendMessageToHost } = useHostAppProxy();

  const { t } = useTranslation();
  const location = useLocation();

  const user = useSelector(state => state.user.user);
  const userProfile = useSelector(state => state.user.userProfile);
  const { flags: featureFlags } = useFeatureFlags();
  const defaultColorMode = useSelector(state => state.app.defaultColorMode);
  const currentBook = useSelector(state => state.book.currentBook);
  const backendReadingData = useSelector(
    state => state.readerApp.backendReadingData
  );
  const readingData = useSelector(state => state.readerApp.readingData);
  const globalPercentagePassed = useSelector(
    state => state.readerApp.percentagePassed
  );
  const currentSpineIndex = useSelector(
    state => state.readerApp.currentSpineIndex
  );
  const fontSizes = useSelector(state => state.app.fontSizes);
  const readingTimeCounters = useSelector(
    state => state.monitor.readingTimeCounters
  );
  const isFocused = useSelector(state => state.monitor.isFocused);
  const isOutsideReader = useSelector(state => state.monitor.isOutsideReader);
  const defaultFontSize = useSelector(state => state.app.defaultFontSize);
  const sidebarVisible = useSelector(state => state.app.sidebarVisible);
  // Reader delivery has a separate global/per-user rollout from generation.
  // Authors retain private preview while that reader rollout is dark.
  const currentContentVersion = useSelector(
    state => state.book.currentContentVersion
  );
  const audioContentVersionId = resolveReaderAudioContentVersionId({
    currentBook,
    currentContentVersion,
  });
  const isBookAuthor = Boolean(
    currentBook?.role === 'author' ||
      currentBook?.author?._id === userProfile?._id
  );
  const readerAudioFeatureEnabled = Boolean(
    featureFlags?.audiobook_reader === true ||
      featureToggleUtil.hasFeatureAccess(userProfile, 'audiobook_reader')
  );
  const audioDeliveryEnabled = shouldPrepareReaderAudio({
    audioAvailable: Boolean(currentBook?.audioAvailable),
    isAuthor: isBookAuthor,
    readerFeatureEnabled: readerAudioFeatureEnabled,
  });
  // Scope the Listen⇄Read DOM anchor lookups to just this chapter's rendered
  // text, so other Draft editors on the page (comment composers) can't pollute
  // ordering / position. Falls back to document if the marker isn't found.
  const readerTextRoot = () =>
    document.querySelector('[data-br-reader-text]') || document;
  const [audioIdToken, setAudioIdToken] = useState(undefined);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioHighlight, setAudioHighlight] = useState(null);
  const [audioViewMode, setAudioViewMode] = useState('listening');
  const [audioDockHeight, setAudioDockHeight] = useState(0);
  const [audioFeedbackSelection, setAudioFeedbackSelection] = useState(null);
  const [audioContinuation, setAudioContinuation] = useState(null);
  const handledNativeNavigationEventRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if (audioDeliveryEnabled) {
      authUtil
        .getFreshIdToken()
        .then(token => !cancelled && setAudioIdToken(token))
        .catch(() => undefined);
    } else {
      setAudioIdToken(undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [audioDeliveryEnabled]);

  const handleAudioEnded = useCallback(() => {
    setAudioPlaying(false);
    const next = continueAfterAudioEnded(currentBook?.parts, currentSpineIndex);
    setAudioContinuation(next);
    if (next) dispatch(updateSpineIndex(next.nextIndex));
  }, [currentBook?.parts, currentSpineIndex, dispatch]);

  const handleSurveyComplete = useCallback(() => {
    setAudioContinuation(current =>
      continueAfterSurvey(currentBook?.parts, currentSpineIndex, current)
    );
  }, [currentBook?.parts, currentSpineIndex]);

  useEffect(() => {
    const event = hostAppSettings.nativeAudioEvent;
    if (
      !audioDeliveryEnabled ||
      !hostAppSettings.isWrapped ||
      !event ||
      handledNativeNavigationEventRef.current === event
    ) {
      return;
    }
    handledNativeNavigationEventRef.current = event;

    if (
      String(event.data?.bookId) !== String(currentBook?._id) ||
      String(event.data?.contentVersionId) !== String(audioContentVersionId)
    ) {
      return;
    }

    if (event.type === 'AUDIO_STATE') {
      setAudioPlaying(Boolean(event.data?.isPlaying));
    } else if (event.type === 'AUDIO_ENDED') {
      setAudioPlaying(false);
    }

    const next = continueFromNativeAudioEvent(
      currentBook?.parts,
      currentSpineIndex,
      event
    );
    if (!next) return;

    setAudioContinuation(next);
    dispatch(updateSpineIndex(next.nextIndex));
  }, [
    audioContentVersionId,
    audioDeliveryEnabled,
    currentBook?._id,
    currentBook?.parts,
    currentSpineIndex,
    dispatch,
    hostAppSettings.isWrapped,
    hostAppSettings.nativeAudioEvent,
  ]);

  useEffect(() => {
    setAudioPlaying(false);
    setAudioHighlight(null);
    setAudioViewMode('listening');
    setAudioFeedbackSelection(null);
    setAudioContinuation(null);
  }, [currentBook?._id, currentSpineIndex]);
  const [currentItemTopPercentagePassed, setCurrentItemTopPercentagePassed] =
    useState();
  const [trackedPercentiles, setTrackedPercentiles] = useState({});

  const shouldNotTrack =
    !currentBook ||
    !userProfile ||
    currentBook.author?._id === userProfile?._id;

  // track calls track if the user is logged in and not the author / query param viewAsReader
  const track = useCallback(
    args => {
      if (!shouldNotTrack) {
        doTrack(args);
      }
    },
    [shouldNotTrack]
  );

  // Use references to allow setTimeout and setInterval functions to access the most recent values:
  const currentSpineIndexRef = useRef(currentSpineIndex);
  currentSpineIndexRef.current = currentSpineIndex;
  const currentBookRef = useRef(currentBook);
  currentBookRef.current = currentBook;
  const userRef = useRef(user);
  userRef.current = user;
  const readingTimeCountersRef = useRef(readingTimeCounters);
  readingTimeCountersRef.current = readingTimeCounters;
  const sessionTimeout = useRef(null);
  const readingTimeMonitor = useRef(null);
  const timeSpentTracker = useRef(null);

  useEffect(() => {
    resetState();
    fetchCurrentBook();
    dispatch(toggleAppSidebar(false));

    const initiateActivityMonitoring = () => {
      // intervals and event listeners
      resumeReadingTimeMonitor();
      // start or resume collecting reading time data when the browser window gets focus
      window.addEventListener('focus', onFocus);
      // stop collecting reading time data when the browser window loses focus
      window.addEventListener('blur', onBlur);
      // track collected data on predefined tracking interval
      timeSpentTracker.current = setInterval(
        doTrackTimeSpent,
        trackingInterval * 1000
      );
      window.addEventListener('scroll', startSessionTimer);
      window.addEventListener('mousemove', startSessionTimer);
    };

    const removeActivityMonitorying = () => {
      pauseReadingTimeMonitor();
      clearTimeout(sessionTimeout.current);
      sessionTimeout.current = null;
      clearInterval(timeSpentTracker.current);
      timeSpentTracker.current = null;
      window.removeEventListener('scroll', startSessionTimer);
      window.removeEventListener('mousemove', startSessionTimer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };

    initiateActivityMonitoring();

    return () => {
      removeActivityMonitorying();
      trackClosedBook(
        user,
        currentBookRef.current,
        currentSpineIndexRef.current
      );
      dispatch(resetReader());
    };
  }, []);

  useEffect(() => {
    if (
      !!user &&
      !!currentBook &&
      !currentBook?.err &&
      currentSpineIndex !== undefined
    ) {
      trackOpenedBook(user, currentBook, currentSpineIndex);
    }
  }, [currentBook, user, currentBook]);

  useEffect(() => {
    if (
      !currentBook?.err &&
      Number.isInteger(currentSpineIndex) &&
      currentBook?.parts &&
      currentBook?.parts[currentSpineIndex]
    ) {
      const currentItem = currentBook.parts[currentSpineIndex];
      if (
        currentItem.kind.includes('Survey', 'Cover') &&
        currentItemTopPercentagePassed !== 0
      ) {
        setCurrentItemTopPercentagePassed(0);
      }
    }
  }, [currentSpineIndex, currentBook]);

  // if focus has changed
  useEffect(() => {
    handleFocusChange({
      isFocused: isFocused,
      isOutsideReader: isOutsideReader,
    });
  }, [isFocused, isOutsideReader]);

  useEffect(() => {
    if (currentBook) {
      const parsedQueryString = queryString.parse(location.search);
      const { part } = parsedQueryString;
      if (part) {
        // if we are asked to go to a specific part, do that
        const partIndex = currentBook.parts.findIndex(
          entry => entry._id === part
        );
        if (partIndex > -1) {
          dispatch(updateSpineIndex(partIndex));
        }
      } else {
        // if else, fetch backend reading data (if any) and use that
        doFetchReadingData();
      }
    }
  }, [currentBook]);

  const onFocus = () => {
    dispatch(updateFocus(true));
  };

  const onBlur = () => {
    dispatch(updateFocus(false));
  };

  useEffect(() => {
    if (
      currentBook?._id !== undefined &&
      readingData !== undefined &&
      backendReadingData !== undefined &&
      currentSpineIndex === undefined
    ) {
      handleReadingdata({
        localReadingData: readingData[user.uid] || {},
        backendReadingData,
      });
    }
  }, [currentBook?._id, backendReadingData, currentSpineIndex]);

  useEffect(() => {
    if (globalPercentagePassed !== undefined && currentBook !== undefined) {
      trackChangedPercentile(user, currentBook, globalPercentagePassed);
    }
  }, [globalPercentagePassed, currentBook]);

  const doFetchReadingData = async () => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(fetchReadingData({ user, idToken }, currentBook._id));
  };

  // update book id if url has changed
  const getCurrentBookId = useCallback(() => {
    const parsedQueryString = queryString.parse(location.search);
    const bookId = parsedQueryString.book;
    return bookId;
  }, [location.search]);

  const resetState = () => {
    setCurrentItemTopPercentagePassed(undefined);
    setTrackedPercentiles({});
  };

  const handleReadingdata = ({ localReadingData, backendReadingData }) => {
    // activate the most completed reached readingdata
    if (
      backendReadingData?.globalPercentagePassed >
      localReadingData[currentBook._id]?.globalPercentagePassed
    ) {
      activateReadingData(backendReadingData);
    } else {
      activateReadingData(localReadingData[currentBook._id]);
    }
  };

  const fetchCurrentBook = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const bookId = getCurrentBookId();
    const parsedQueryString = queryString.parse(location.search);
    const { currentContentVersionId } = parsedQueryString;
    dispatch(
      fetchCurrentBookAsReader(idToken, bookId, currentContentVersionId)
    );
  };

  const percentagePassedUpdated = percentageData => {
    const percentagePassed = percentageData.bottom; // use this for global progress bar etc
    const currentItem = currentBook.parts[currentSpineIndex];
    let updatedPercentagePassedGlobally = undefined;
    setCurrentItemTopPercentagePassed(percentageData.top);
    if (['Survey', 'Cover'].includes(currentItem.kind)) {
      // updateGlobalPercentagePassed(percentagePassed);
    } else {
      const wordsPassedLocally =
        currentBook.parts[currentSpineIndex].wordCount * percentagePassed;
      const wordsPassedGlobally =
        currentBook.parts[currentSpineIndex].aggregatedWordCount -
        currentBook.parts[currentSpineIndex].wordCount +
        wordsPassedLocally;
      updatedPercentagePassedGlobally =
        wordsPassedGlobally / currentBook.wordCount;
      // updateGlobalPercentagePassed(percentagePassedGlobally);
    }
    doSaveReadingData(false, updatedPercentagePassedGlobally);
  };

  const doSaveReadingData = async (
    forceSaveToBackend,
    updatedPercentagePassedGlobally
  ) => {
    if (shouldNotTrack) {
      return;
    }
    const idToken = await authUtil.getFreshIdToken();
    if (
      user !== undefined &&
      idToken !== undefined &&
      currentBook !== undefined
    ) {
      const newReadingData = {
        chapter: currentBook.parts[currentSpineIndex]._id,
        percentagePassed: currentItemTopPercentagePassed,
        part: {
          id: currentBook.parts[currentSpineIndex]._id,
          percentagePassed: currentItemTopPercentagePassed,
        },
        globalPercentagePassed:
          updatedPercentagePassedGlobally !== undefined
            ? updatedPercentagePassedGlobally
            : 0,
        timestamp: new Date().toJSON(),
      };
      dispatch(
        saveReadingData(
          user,
          idToken,
          currentBook._id,
          newReadingData,
          forceSaveToBackend
        )
      );
    }
  };

  const activateReadingData = readingData => {
    dispatch(
      updateReadingData(user, currentBook._id, readingData, () => {
        const newSpineIndex =
          readingData !== undefined
            ? currentBook.parts.findIndex(part => {
                return part._id === readingData.chapter;
              })
            : 0;
        const newPercentagePassed =
          readingData !== undefined ? readingData.percentagePassed : 0;
        dispatch(updateSpineIndex(newSpineIndex));
        setCurrentItemTopPercentagePassed(newPercentagePassed);
      })
    );
  };

  const doAddTimeSpent = seconds => {
    try {
      const book = currentBookRef?.current;
      const currentUser = userRef.current;
      const spineIndex = currentSpineIndexRef.current;
      if (!currentUser?.uid || !book || book?.err) return;
      const visibleParagraphs = getVisibleParagraphs();
      const currentChapter = book.parts[spineIndex];
      if (!currentChapter) return;
      visibleParagraphs.forEach(paragraph => {
        const data = {
          event: { id: TIME_SPENT, count: seconds / visibleParagraphs.length },
          user: { id: currentUser.uid },
          book: { id: book._id },
          chapter: { id: currentChapter._id },
          paragraph: { number: paragraph.number },
        };
        dispatch(addTimeSpent(data));
      });
    } catch (err) {
      console.error(err);
    }
  };

  const doTrackTimeSpent = () => {
    if (shouldNotTrack) {
      return;
    }
    dispatch(trackTimeSpent(readingTimeCountersRef.current));
  };

  const getVisibleParagraphs = () => {
    const chapterParagraphs = document.getElementsByClassName('br-cp');
    const visibleParagraphs = [];
    for (let index = 0; index < chapterParagraphs.length; index++) {
      const paragraph = chapterParagraphs[index];
      // const paragraphNumber = parseInt(paragraph.dataset.pn);
      const paragraphNumber = getParagraphNumber(paragraph);
      if (isElementInViewport(paragraph)) {
        visibleParagraphs.push({ number: paragraphNumber });
      }
    }
    return visibleParagraphs;
  };

  const getParagraphNumber = paragraphElement => {
    const paragraphRegex = /(br-pn)([0-9]*)/;
    const classes = paragraphElement.className;
    const match = classes.match(paragraphRegex);
    const paragraphNumber = parseInt(match[2]);
    return paragraphNumber;
  };

  const startSessionTimer = () => {
    clearTimeout(sessionTimeout.current);
    // if there is no reading time monitor, create it
    resumeReadingTimeMonitor();
    sessionTimeout.current = setTimeout(function () {
      sessionTimeout.current = null;
      pauseReadingTimeMonitor();
    }, maxInActivityTime * 1000);
  };

  /*
   * Returns true if at least half the element is within the viewport from a vertical perspective.
   */
  const isElementInViewport = el => {
    var rect = el.getBoundingClientRect();

    const fullHeight = rect.bottom - rect.top;
    const fivePercentOfHeight = fullHeight * 0.05;
    const footerHeight = 52;
    const headerHeight = 43;

    return (
      rect.top >= 0 - (fullHeight - fivePercentOfHeight - footerHeight) &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) +
          (fullHeight - fivePercentOfHeight - headerHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  const handleFocusChange = ({ isFocused, isOutsideReader }) => {
    if (isFocused && !isOutsideReader) {
      resumeReadingTimeMonitor();
    } else {
      pauseReadingTimeMonitor();
    }
  };

  const resumeReadingTimeMonitor = () => {
    if (readingTimeMonitor.current !== null) return;
    clearInterval(readingTimeMonitor.current);
    readingTimeMonitor.current = setInterval(function () {
      doAddTimeSpent(monitoringInterval);
    }, monitoringInterval * 1000);
  };

  const pauseReadingTimeMonitor = () => {
    clearInterval(readingTimeMonitor.current);
    readingTimeMonitor.current = null;
  };

  const trackOpenedBook = (user, book, spineIndex) => {
    /*
    * removed this because it messes up session tracking (if fired at the same time as another event and the session has expired, we will get one new session for this event and one for the other)
    const visibleParagraphs = getVisibleParagraphs();
   
    track({
      book: book._id,
      data: {
        event: {id: OPENED_BOOK},
        user: {id: user.uid},
        book: {id: book._id},
        chapter: {id: book.parts[spineIndex || 0]._id},
        paragraph: {number: visibleParagraphs.length > 0 ? visibleParagraphs[0].number : null}
      }
    })
    */
  };

  const trackClosedBook = (user, book, spineIndex) => {
    if (!user?.uid || !book?.parts) return;
    const visibleParagraphs = getVisibleParagraphs();
    const trackingData = {
      event: { id: CLOSED_BOOK },
      user: { id: user.uid },
      book: { id: book._id },
      paragraph: {
        number:
          visibleParagraphs.length > 0 ? visibleParagraphs[0].number : null,
      },
    };
    const part = book.parts[spineIndex || 0];
    if (part && part.type === 'Chapter') {
      trackingData.chapter = { id: part._id };
    }
    track({
      book: book._id,
      data: [trackingData],
    });
  };

  const getPercentile = percentage => Math.floor(percentage * 10) * 10;

  const trackChangedPercentile = (user, book, percentage) => {
    const percentile = getPercentile(percentage);
    if (trackedPercentiles[percentile.toString()]) {
      return;
    } else {
      let updatedTrackedPercentiles = { ...trackedPercentiles };
      updatedTrackedPercentiles[percentile.toString()] = true;
      setTrackedPercentiles({ ...updatedTrackedPercentiles });
      track({
        book: book._id,
        data: [
          {
            event: { id: REACHED_PERCENTILE, count: percentile },
            user: { id: user.uid },
            book: { id: book._id },
          },
        ],
      });
    }
  };

  const getCurrentColorMode = useCallback(() => {
    return userProfile?.readerSettings?.colorMode ?? defaultColorMode;
  }, [userProfile?.readerSettings?.colorMode]);

  const getFontSizeEm = useCallback(() => {
    const currentFontSize =
      userProfile?.readerSettings?.fontSize ?? defaultFontSize;
    return fontSizes.find(size => size.name === currentFontSize)?.size ?? 1;
  }, [fontSizes, userProfile?.readerSettings?.fontSize]);
  if (!currentBook) {
    return (
      <div
        className="container"
        role="status"
        style={{ position: 'absolute', top: 200 }}
      >
        {t('Loading', 'Loading')}...
      </div>
    );
  }
  if (currentBook?.err) {
    return (
      <div className="container">
        <Alert variant="error">{currentBook.err}</Alert>
      </div>
    );
  }
  if (currentSpineIndex !== undefined) {
    const currentPart = currentBook.parts[currentSpineIndex];
    const isProtectedReader =
      currentBook.role === 'reader' || currentBook.access === 'comment';
    return (
      <ReaderWrapper
        fontSize={getFontSizeEm()}
        data-br-protected-reader={isProtectedReader ? 'true' : undefined}
        onCopy={isProtectedReader ? blockReaderContentAction : undefined}
        onCut={isProtectedReader ? blockReaderContentAction : undefined}
        onContextMenu={event =>
          blockReaderContextMenu(event, {
            isProtectedReader,
            isSmallScreen: appUtil.isSmallScreen(),
          })
        }
        onKeyDown={isProtectedReader ? blockReaderPrintShortcut : undefined}
      >
        <div style={{ marginBottom: '1em' }}>
          <ReaderProgress
            aria-label={t('ReadingProgress', 'Reading progress')}
            max={100}
            value={Math.max(
              0,
              Math.min(100, Math.round((globalPercentagePassed || 0) * 100))
            )}
            className="reader-progress-bar"
          />
        </div>
        <PaddedWrapper>
          {currentPart.kind === 'Cover' && (
            <MaxWidthWrapper>
              <Cover
                cover={currentPart}
                percentagePassedUpdated={percentagePassedUpdated}
              />
            </MaxWidthWrapper>
          )}
          {currentPart.kind === 'Chapter' && (
            <ChapterWrapper
              sidebarVisible={sidebarVisible}
              $audioDockHeight={audioDockHeight}
            >
              {
                // add padding div when sidebar is not visible in order to center text content in grid
                // (this div takes the left-most column)
                !sidebarVisible && <div />
              }
              <div>
                <div>
                  <ReaderTitle>{currentBook.parts[currentSpineIndex].title}</ReaderTitle>
                  {audioDeliveryEnabled && (
                    <ReaderAudioAccess
                      audioAvailable={Boolean(currentBook.audioAvailable)}
                      isAuthor={isBookAuthor}
                      isWrapped={hostAppSettings.isWrapped}
                    >
                      {audioContentVersionId && audioIdToken ? (
                        <ChapterAudioPlayer
                          key={currentBook.parts[currentSpineIndex]._id}
                          bookId={currentBook._id}
                          contentVersionId={audioContentVersionId}
                          chapter={currentBook.parts[currentSpineIndex]}
                          userId={userProfile && userProfile._id}
                          idToken={audioIdToken}
                          nativeAudioBridge={
                            hostAppSettings.isWrapped
                              ? {
                                  sendMessage: sendMessageToHost,
                                  event: hostAppSettings.nativeAudioEvent,
                                }
                              : null
                          }
                          nativeContinuationQueue={getNativeAudioContinuationQueue(
                            currentBook.parts,
                            currentSpineIndex
                          )}
                          bookTitle={currentBook.title}
                          authorName={currentBook.authorName}
                          colorMode={getCurrentColorMode()}
                          enabled
                          autoStart={
                            audioContinuation?.autoStartPartId ===
                            currentBook.parts[currentSpineIndex]._id
                          }
                          onAutoStartHandled={() => setAudioContinuation(null)}
                          onPlaybackStateChange={setAudioPlaying}
                          onDockHeightChange={setAudioDockHeight}
                          onPlaybackAnchorChange={setAudioHighlight}
                          readingFeedbackSelection={audioFeedbackSelection}
                          onViewModeChange={mode => {
                            setAudioViewMode(mode);
                            if (mode === 'reading') setAudioPlaying(false);
                            if (mode === 'listening') {
                              setAudioFeedbackSelection(null);
                            }
                          }}
                          onEnded={
                            hostAppSettings.isWrapped
                              ? undefined
                              : handleAudioEnded
                          }
                          onSubmitComment={({
                            kind,
                            aiOrigin,
                            text,
                            reaction,
                            inlineInfo,
                          }) =>
                            dispatch(
                              submitComment(
                                audioIdToken,
                                currentBook._id,
                                currentBook.parts[currentSpineIndex]._id,
                                text || null,
                                { kind, reaction, inlineInfo, aiOrigin }
                              )
                            )
                          }
                          // Listen ⇄ Read toggle (§7.5): read the rendered text's
                          // anchors fresh from the DOM at click time, scoped to
                          // THIS chapter's text (never other editors on the page),
                          // via the adapter for the book's format (draftjs blocks
                          // vs tiptap pids).
                          getTextPosition={() =>
                            getReaderTextPosition(
                              readerTextRoot(),
                              currentBook.contentFormat
                            )
                          }
                          orderedKeys={() =>
                            getOrderedKeys(
                              readerTextRoot(),
                              currentBook.contentFormat
                            )
                          }
                          onSwitchToRead={anchor =>
                            scrollReaderToAnchor(
                              anchor,
                              readerTextRoot(),
                              currentBook.contentFormat
                            )
                          }
                        />
                      ) : null}
                    </ReaderAudioAccess>
                  )}
                  <ReaderTextStage
                    data-br-reader-text="true"
                    data-audio-playing={audioPlaying ? 'true' : 'false'}
                  >
                    <TextSegment
                      percentagePassedUpdated={percentagePassedUpdated}
                      text={currentBook.parts[currentSpineIndex].html}
                      chapter={currentBook.parts[currentSpineIndex]} // todo: change this to '.text' or '.content'
                      initialPercentagePassed={currentItemTopPercentagePassed}
                      dir={language.getTextDirection(currentBook.language)}
                      audioHighlight={audioHighlight}
                      audioFeedbackDockActive={audioViewMode === 'reading'}
                      onAudioFeedbackSelectionChange={setAudioFeedbackSelection}
                    />
                  </ReaderTextStage>
                </div>
                <ReaderDivider aria-hidden="true" />
                <ChapterMenu standAlone />
                {currentBook.enableChapterComments &&
                  currentPart.kind === 'Chapter' && [
                    <ReaderDivider key="divider" aria-hidden="true" />,
                    <h3
                      key="header"
                      style={{
                        borderBottom: '1px solid var(--br-border)',
                        paddingBottom: '0.4em',
                      }}
                    >
                      {t('Comments')}
                    </h3>,
                    <ChapterCommentsList
                      key="chaptercomments"
                      filter="ChapterComment"
                    />,
                  ]}
              </div>
              <Media greaterThanOrEqual="tablet">
                {(className, renderChildren) =>
                  renderChildren && (
                    <SidebarCommentsWrapper>
                      {currentPart.kind === 'Chapter' && [
                        <h4 className="br-text" key="comments-header">
                          {t('Comments')}
                        </h4>,
                        <InlineCommentsList
                          positioning="sidebar"
                          key="comments"
                          disableLeavingComments
                          showOnly={{ type: 'InlineComment' }}
                        />,
                      ]}
                    </SidebarCommentsWrapper>
                  )
                }
              </Media>
              <Media lessThan="tablet">
                {(className, renderChildren) =>
                  renderChildren && (
                    <InlineCommentsList
                      positioning="bottom"
                      key="comments"
                      disableLeavingComments
                      enableFilters={false}
                      showOnly={{ type: 'InlineComment' }}
                    />
                  )
                }
              </Media>
            </ChapterWrapper>
          )}
          {currentPart.kind === 'Survey' && (
            <ChapterWrapper sidebarVisible={sidebarVisible}>
              {
                // add padding div when sidebar is not visible in order to center text content in grid
                // (this div takes the left-most column)
                !sidebarVisible && <div />
              }
              <div>
                <ReaderTitle>{currentBook.parts[currentSpineIndex].title}</ReaderTitle>
                <Survey percentagePassedUpdated={percentagePassedUpdated} />
                <ReaderDivider aria-hidden="true" />
                <ChapterMenu
                  standAlone
                  onSurveyComplete={handleSurveyComplete}
                />
              </div>
            </ChapterWrapper>
          )}
        </PaddedWrapper>
      </ReaderWrapper>
    );
  }
  return null;
};

export default Reader;
